import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Save, RefreshCw, User, Eye, Edit, FileText, Building2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Permissions = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      fetchUserPermissions(selectedUserId);
    }
  }, [selectedUserId]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out admin users (they always have full permissions)
      const nonAdminUsers = response.data.filter(user => user.role !== 'admin');
      setUsers(nonAdminUsers);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch users');
      setLoading(false);
    }
  };

  const fetchUserPermissions = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/${userId}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data);
      setSelectedUser(users.find(u => u.id === userId));
    } catch (error) {
      toast.error('Failed to fetch user permissions');
    }
  };

  const handlePermissionChange = (section, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [`${section}_${action}`]: value
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user first');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare update data (exclude metadata fields)
      const updateData = {
        flights_view: permissions.flights_view,
        flights_edit: permissions.flights_edit,
        flights_save: permissions.flights_save,
        flights_create: permissions.flights_create,
        reports_view: permissions.reports_view,
        reports_edit: permissions.reports_edit,
        reports_save: permissions.reports_save,
        reports_create: permissions.reports_create,
        agencies_view: permissions.agencies_view,
        agencies_edit: permissions.agencies_edit,
        agencies_save: permissions.agencies_save,
        agencies_create: permissions.agencies_create
      };

      await axios.put(`${API}/users/${selectedUserId}/permissions`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Permissions updated successfully! User must log out and log back in for changes to take effect.`, {
        duration: 6000
      });
      fetchUserPermissions(selectedUserId); // Refresh to show updated data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const renderPermissionToggle = (section, action, label) => {
    const permissionKey = `${section}_${action}`;
    const isEnabled = permissions?.[permissionKey] ?? true;

    return (
      <div className="flex items-center justify-between p-3 rounded" style={{ background: 'rgba(74, 90, 138, 0.2)' }}>
        <Label style={{ color: '#E8E8E8', fontWeight: '500' }}>{label}</Label>
        <Button
          size="sm"
          onClick={() => handlePermissionChange(section, action, !isEnabled)}
          style={{
            background: isEnabled ? '#22C55E' : '#EF4444',
            color: '#FFFFFF',
            minWidth: '80px'
          }}
        >
          {isEnabled ? 'Enabled' : 'Disabled'}
        </Button>
      </div>
    );
  };

  const renderSectionPermissions = (section, icon, title) => {
    const Icon = icon;
    return (
      <Card style={{ background: '#0A1128', borderColor: '#4A5A8A' }}>
        <CardHeader style={{ borderBottom: '1px solid #4A5A8A' }}>
          <CardTitle className="flex items-center gap-2" style={{ color: '#D4AF37', fontSize: '1.1rem' }}>
            <Icon className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-6">
          {renderPermissionToggle(section, 'view', 'View')}
          {renderPermissionToggle(section, 'create', 'Create')}
          {renderPermissionToggle(section, 'edit', 'Edit')}
          {renderPermissionToggle(section, 'save', 'Save')}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" style={{ color: '#D4AF37' }} />
          <p style={{ color: '#B0B0B0' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#D4AF37' }}>
            <Shield className="inline h-7 w-7 mr-2" />
            User Permissions Management
          </h1>
          <p style={{ color: '#B0B0B0' }}>
            Manage user-specific permissions for Flights, Reports, and Agencies sections.
          </p>
        </div>
      </div>

      {/* User Selection */}
      <Card style={{ background: '#0A1128', borderColor: '#4A5A8A' }}>
        <CardHeader>
          <CardTitle style={{ color: '#D4AF37', fontSize: '1.1rem' }}>
            Select User
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label style={{ color: '#E8E8E8', marginBottom: '8px', display: 'block' }}>User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue placeholder="Select a user..." />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  {users.length === 0 ? (
                    <SelectItem value="none" disabled>No non-admin users found</SelectItem>
                  ) : (
                    users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.full_name} ({user.email}) - {user.role}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedUser && (
              <div className="flex items-center gap-3 p-4 rounded" style={{ background: 'rgba(74, 90, 138, 0.2)' }}>
                <User className="h-8 w-8" style={{ color: '#D4AF37' }} />
                <div>
                  <p style={{ color: '#E8E8E8', fontWeight: '600' }}>{selectedUser.full_name}</p>
                  <p style={{ color: '#B0B0B0', fontSize: '0.875rem' }}>{selectedUser.email}</p>
                  <p style={{ color: '#60A5FA', fontSize: '0.875rem', textTransform: 'capitalize' }}>
                    Role: {selectedUser.role}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Permissions Grid */}
      {permissions && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {renderSectionPermissions('flights', FileText, 'Flights Permissions')}
            {renderSectionPermissions('reports', Edit, 'Reports Permissions')}
            {renderSectionPermissions('agencies', Building2, 'Agencies Permissions')}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              onClick={() => fetchUserPermissions(selectedUserId)}
              variant="outline"
              style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={handleSavePermissions}
              disabled={saving}
              style={{ background: '#22C55E', color: '#FFFFFF' }}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Permissions'}
            </Button>
          </div>

          {/* Info Note */}
          <Card style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: '#EF4444' }}>
            <CardContent className="pt-6">
              <p style={{ color: '#EF4444', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                ⚠️ IMPORTANT: User Must Log Out and Log Back In
              </p>
              <p style={{ color: '#E8E8E8', fontSize: '0.875rem' }}>
                After changing permissions, the affected user must <strong>log out and log back in</strong> for the menu changes to take effect. 
                Permissions are loaded during login and cached until the next login.
              </p>
              <p style={{ color: '#B0B0B0', fontSize: '0.875rem', marginTop: '8px' }}>
                <strong>Note:</strong> Admin users always have full permissions and cannot be modified. 
                Permissions apply to: Flights, Reports, and Agencies sections.
              </p>
            </CardContent>
          </Card>
        </>
      )}

      {!permissions && selectedUserId && (
        <Card style={{ background: '#0A1128', borderColor: '#4A5A8A' }}>
          <CardContent className="pt-6 text-center">
            <p style={{ color: '#B0B0B0' }}>Loading permissions...</p>
          </CardContent>
        </Card>
      )}

      {!selectedUserId && (
        <Card style={{ background: '#0A1128', borderColor: '#4A5A8A' }}>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4" style={{ color: '#4A5A8A' }} />
            <p style={{ color: '#B0B0B0' }}>Select a user to manage their permissions</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Permissions;
